// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useContext } from 'react';
import { firestore } from '../../context/FirebaseConfig';
import { collectionGroup, query, orderBy, limit, getDocs, where, startAfter, getCountFromServer, collection, Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './PlantProcessTab.scss';
import MaterialFlowModal from '../MaterialFlowModal/MaterialFlowModal';
import { AuthContext } from '../../context/AuthContext';
import { getBaleFeedSettings } from '../../shared_components/utils/FirebaseUtils';
import { getCameraTimestampFromBaleFeedTimestamp } from '../MaterialFlow/MaterialFlowUtils';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';

function PlantProcessTab({ supplier, supplierData, dateRange = { startDate: null, endDate: null }, activeFilters = {} }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, labelsets, logEventCustom } = useContext(AuthContext);
  const [plantFeedEntries, setPlantFeedEntries] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [lastDoc, setLastDoc] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);
  const [expandedEntryId, setExpandedEntryId] = useState(null);
  const [baleFeedSettings, setBaleFeedSettings] = useState(null);
  const [balesCounts, setBalesCounts] = useState({});

  // State for modal control
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedMaterialFlowView, setSelectedMaterialFlowView] = React.useState(null);
  
  const historyContainerRef = React.useRef(null);
  
  const PAGE_SIZE = 10;

  async function fetchPlantFeedEntries(supplierId, lastDocument = null) {
    // Create base query
    let queryConstraints = [
      where("supplier_id", "==", supplierId),
      orderBy("start_timestamp", "desc"),
      limit(PAGE_SIZE)
    ];
    
    // Add date range filters if available
    if (dateRange.startDate) {
      const startTimestamp = Timestamp.fromDate(new Date(dateRange.startDate));
      queryConstraints.push(where("start_timestamp", ">=", startTimestamp));
    }
    
    if (dateRange.endDate) {
      const endTimestamp = Timestamp.fromDate(new Date(dateRange.endDate));
      queryConstraints.push(where("start_timestamp", "<=", endTimestamp));
    }

    if (activeFilters?.material && activeFilters?.material !== 'all') {
      queryConstraints.push(where("material", "==", activeFilters?.material));
    }
    
    let q = query(
      collectionGroup(firestore, "bale_feed_timestamps"),
      ...queryConstraints
    );
    
    // Paginate data
    if (lastDocument) {
      q = query(q, startAfter(lastDocument));
    }
    
    const docs = await getDocs(q);
    
    // Check if we have more documents to load
    setHasMore(docs.size === PAGE_SIZE);
    
    // Get the last document for pagination
    const lastVisible = docs.docs[docs.docs.length - 1];
    if (lastVisible) {
      setLastDoc(lastVisible);
    }
    
    return docs;
  }

  // Get number of bales processed for a given bale feed entry
  async function getNumBalesProcessed(bale_feed_entry_id) {
    const q = query(
      collection(firestore, "clients", currentUser.company.company, "observations"),
      where("analytics.bale_feed_supplier_matching.matched_bale_feed_doc_id", "==", bale_feed_entry_id)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  // Load bale counts for new entries
  React.useEffect(() => {
    async function loadNewBaleCounts() {
      if (!currentUser?.company?.company) return;
      
      const entryIds = Object.keys(plantFeedEntries);
      if (entryIds.length === 0) return;
      
      // Filter out entries that already have counts
      const newEntryIds = entryIds.filter(id => balesCounts[id] === undefined);
      if (newEntryIds.length === 0) return;
      
      const newCounts = {...balesCounts};
      const countPromises = newEntryIds.map(async (id) => {
        try {
          const count = await getNumBalesProcessed(id);
          newCounts[id] = count;
        } catch (error) {
          console.error(`Error fetching bale count for entry ${id}:`, error);
          newCounts[id] = 0;
        }
      });
      
      await Promise.all(countPromises);
      setBalesCounts(newCounts);
    }
    
    if (!isLoading && Object.keys(plantFeedEntries).length > 0) {
      loadNewBaleCounts();
    }
  }, [plantFeedEntries, isLoading, currentUser?.company?.company, balesCounts]);

  React.useEffect(() => {
    async function fetchLatestPlantFeedForSupplier(supplierId) {
      setIsLoading(true);
      // Reset state when date range changes
      setPlantFeedEntries({});
      setLastDoc(null);
      setHasMore(true);
      
      try {
        const docs = await fetchPlantFeedEntries(supplierId);
        if (docs) {
          const entries = {};
          docs.forEach((doc) => {
            entries[doc.id] = doc.data();
          });
          setPlantFeedEntries(entries);
        }
      } catch (error) {
        console.error("Error fetching plant feed entries:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchLatestPlantFeedForSupplier(supplier.id);
  }, [supplier.id, dateRange]);

  async function loadMoreEntries() {
    if (!hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      const docs = await fetchPlantFeedEntries(supplier.id, lastDoc);
      if (docs) {
        const newEntries = { ...plantFeedEntries };
        docs.forEach((doc) => {
          newEntries[doc.id] = doc.data();
        });
        setPlantFeedEntries(newEntries);
      }
    } catch (error) {
      console.error("Error loading more entries:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }

  // Handle scrolling to load more entries
  React.useEffect(() => {
    const container = historyContainerRef.current;
    if (!container) return;

    function handleScroll() {
      // Check if user has scrolled to the bottom (with a 50px threshold)
      if (container.scrollHeight - container.scrollTop <= container.clientHeight + 50) {
        if (hasMore && !isLoadingMore) {
          loadMoreEntries();
        }
      }
    }

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoadingMore, lastDoc]);

  // Load bale-feed-settings
  React.useEffect(() => {
    async function loadBaleFeedSettings() {
      setBaleFeedSettings(await getBaleFeedSettings(firestore, currentUser.company.company));
    }
    loadBaleFeedSettings();
  }, [supplier.company]);

  function formatDate(timestamp) {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  async function handleOpenMaterialFlowModal(id, entry) {
    let middleTimestamp = null;
    if (entry.start_timestamp && entry.end_timestamp) {
      const startDate = entry.start_timestamp.toDate();
      const endDate = entry.end_timestamp.toDate();
      middleTimestamp = new Date((startDate.getTime() + endDate.getTime()) / 2);
    } else if (entry.start_timestamp) {
      middleTimestamp = entry.start_timestamp.toDate();
    }

    if (!middleTimestamp) {
      return;
    }

    let timestampAtCamera = await getCameraTimestampFromBaleFeedTimestamp(
      firestore,
      currentUser.company.company,
      entry.device_id,
      middleTimestamp
    )

    if (!timestampAtCamera) {
      console.error("No timestamp at camera found")
      timestampAtCamera = middleTimestamp;
    }

    const timestampIso = timestampAtCamera.toISOString();
    
    if (timestampIso) {
      setSelectedMaterialFlowView({
        deviceId: entry.device_id,
        timestamp: timestampIso
      });
      setIsModalOpen(true);
    }

    logEventCustom('select_content', {content_type: 'supplier_insights_material_flow', content_id: id});
  }
  
  function handleCloseModal() {
    setIsModalOpen(false);
  }

  // Toggle card expansion
  function handleToggleExpand(entryId) {
    setExpandedEntryId(prevId => (prevId === entryId ? null : entryId));
  }

  // Function to get material display name
  function getMaterialDisplayName(materialKey) {
    return labelsets?.materials?.labels?.[materialKey]?.display_name || materialKey || 'Unknown';
  }

  // Function to get field display name
  function getFieldDisplayName(fieldKey) {
    return `Field ${labelsets?.stock_field?.labels?.[fieldKey]?.display_name || fieldKey || 'Unknown'}`;
  }

  function getShiftWorkerDisplayName(shiftWorkerKey) {
    const firstName = baleFeedSettings?.users?.[shiftWorkerKey]?.firstname;
    const lastName = baleFeedSettings?.users?.[shiftWorkerKey]?.lastname;
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return shiftWorkerKey || 'Unknown';
  }

  function renderEntryCard(id, entry) {
    // Use the fetched bale count if available, otherwise fall back to the entry.bales_count or 0
    const balesCount = balesCounts[id] !== undefined ? balesCounts[id] : (entry.bales_count || 0);
    const materialKey = entry.material;
    const field = entry.field;
    const isExpanded = expandedEntryId === id;
    const shiftName = entry.shift_info?.work_shift?.name || 'N/A';
    const shiftWorker = entry.shift_info?.user || 'N/A';

    return (
      <div 
        key={id} 
        className={`entry-card ${isExpanded ? 'expanded' : ''}`}
        onClick={() => handleToggleExpand(id)}
      >        
        <div className="entry-grid">
          <div className="entry-content">
            <div className="truck-icon-container">
              <div className="truck-icon">
                <LocalShippingIcon className="icon" />
              </div>
              <div className="date">
                {formatDate(entry.start_timestamp)}
              </div>
            </div>

            <div className="bales-info-line">
              <span className="count">{balesCount}</span> {t('bales')} {t('processed')}
            </div>
            <div className="badge-container">
              {materialKey && (
                <span className="material-badge">
                  {getMaterialDisplayName(materialKey)}
                </span>
              )}
              {field && (
                <span className="field-info">
                  {getFieldDisplayName(field)}
                </span>
              )}
            </div>
          </div>
          
          <div className="entry-actions">
            <button 
              className="material-flow-button"
              onClick={(e) => { 
                e.stopPropagation();
                handleOpenMaterialFlowModal(id, entry);
              }}
            >
              {t('material_flow_show_in')}
            </button>
          </div>
        </div>

        {/* Expanded Details - Render conditionally */}
        {isExpanded && (
          <div className="expanded-details">
            <table className="details-table">
              <tbody>
                <tr className="detail-row">
                  <td className="detail-label-cell">{t('property_material')}:</td>
                  <td className="detail-value-cell">{getMaterialDisplayName(materialKey)}</td>
                </tr>
                <tr className="detail-row">
                  <td className="detail-label-cell">{t('processing_field')}:</td>
                  <td className="detail-value-cell">{getFieldDisplayName(field)}</td>
                </tr>
                <tr className="detail-row">
                  <td className="detail-label-cell">{t('processing_shift')}:</td>
                  <td className="detail-value-cell">{shiftName}</td>
                </tr>
                <tr className="detail-row">
                  <td className="detail-label-cell">{t('processing_shift_worker')}:</td>
                  <td className="detail-value-cell">{getShiftWorkerDisplayName(shiftWorker)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-plant-process animate-fade-in">
      <h3>
        {t('plant_process_history')}
        {activeFilters?.material && activeFilters?.material !== 'all' && (
          <span className="material-filter-badge">
            {getMaterialDisplayName(activeFilters?.material)}
          </span>
        )}
      </h3>
      
      {isLoading ? (
        <div className="loading-message">{t('loading_processing_history')}</div>
      ) : Object.keys(plantFeedEntries).length === 0 ? (
        <div className="empty-message">{t('no_processing_history')}</div>
      ) : (
        <div className="history-container" ref={historyContainerRef}>
          <div className="entries-container">
            {Object.entries(plantFeedEntries).map(([id, entry]) => renderEntryCard(id, entry))}
          </div>
          
          {isLoadingMore && (
            <div className="loading-more">{t('loading_more')}</div>
          )}
          
          {!hasMore && Object.keys(plantFeedEntries).length > 0 && (
            <div className="loading-more">{t('no_more_entries')}</div>
          )}
        </div>
      )}
      
      {/* Use the new MaterialFlowModal component */}
      <MaterialFlowModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        timestamp={selectedMaterialFlowView?.timestamp}
        deviceId={selectedMaterialFlowView?.deviceId}
      />
    </div>
  );
}

export default PlantProcessTab; 